import { Component, OnInit, Injectable, signal, computed, inject, ElementRef, HostListener } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from '../auth/auth.service';
import { UtilService } from 'src/app/services/util.service';
import { UnsavedChangesService } from 'src/app/services/unsaved-changes.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { OrganisationService } from 'src/app/services/organisation.service';
import { NavStateService } from 'src/app/services/nav-state.service';
import { ModalSelectOrganisationComponent } from 'src/app/modals/modal-select-organisation/modal-select-organisation.component';
import { NgbModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { take } from 'rxjs/operators';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { UserHelperService } from '../shared/user-helper.service';
declare var $: any;

@Component({
  selector: 'app-navigation',
  standalone: true,
  imports: [CommonModule, TranslateModule, NgbDropdownModule, RouterModule],
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.css'
})

export class NavigationComponent implements OnInit {
  // Services
  private authService = inject(AuthService);
  private utilService = inject(UtilService);
  private modalService = inject(NgbModal);
  private translate = inject(TranslateService);
  private organisationService = inject(OrganisationService);
  public router = inject(Router);
  private unsavedChangesService = inject(UnsavedChangesService);
  private navStateService = inject(NavStateService);
  private userHelper = inject(UserHelperService);

  // Signals for reactive state
  currentUser = signal<any>(null);
  selectedOrganisation = signal<any>(null);
  selectedDepartment = signal<any>(null);
  organisationList = signal<any[]>([]);

  // Navigation state signals
  isChangeOrganisation = signal(false);
  isHome = signal(false);
  isCatalogue = signal(false);
  isProducts = signal(false);
  isMaterialList = signal(false);
  isInventory = signal(false);
  isReports = signal(false);
  isAssessment = signal(false);
  isAdministration = signal(false);
  isLaw = signal(false);
  isSamtec = signal(false);
  isEducation = signal(false);
  isWebshop = signal(false);
  isControlPanel = signal(false);
  isKoncernHome = signal(false);
  isAdministrationDropdownOpen = signal(false);
  isAssessmentDropdownOpen = signal(false);

  // Subscription flags
  subscribesToCatalogue = signal(false);
  subscribesToProducts = signal(false);
  subscribesToActivities = signal(false);
  subscribesToChecklist = signal(false);
  subscribesToRiskAssessment = signal(false);
  subscribesToProjects = signal(false);
  subscribesToExposures = signal(false);
  subscribesToPurchase = signal(false);
  subscribesToSubstitution = signal(false);
  subscribesToDistribution = signal(false);
  subscribesToSamtec = signal(false);
  subscribesToLawLawpoint = signal(false);
  subscribesToLawAptor = signal(false);
  subscribesToCoordinator = signal(false);
  subscribesToReports = signal(false);
  subscribesToEducation = signal(false);
  subscribesToProductApplication = signal(false);
  subscribesToInventory = signal(false);

  // Computed values
  isGuest = computed(() => this.currentUser()?.is_guest || false);
  isPrinter = computed(() => this.currentUser()?.is_printer || false);
  isSuperUser = computed(() => this.currentUser()?.is_super_user || false);
  isSuperAdmin = computed(() => this.currentUser()?.is_super_admin || false);
  isGroupAdmin = computed(() => {
    const u = this.currentUser();
    return !!(u && (u.is_group_admin === 1 || u.is_group_admin === true || u.is_group_admin === '1') && u.group_id);
  });
  /** Thin koncern shell: Change company + koncern items only. */
  isKoncernMode = computed(() =>
    this.isGroupAdmin() && !this.userHelper.isGroupAdminCompanyMode()
  );
  canChangeOrganisation = computed(() => this.isSuperAdmin() || this.isGroupAdmin());
  /** Koncernanvändare switch only between the companies in their koncern. */
  isGroupCompanySwitcher = computed(() => this.isGroupAdmin() && !this.isSuperAdmin());
  isAdmin = computed(() =>
    this.currentUser()?.is_admin === 1 ||
    this.currentUser()?.is_local_admin === 1 ||
    this.currentUser()?.is_super_user === 1 ||
    this.currentUser()?.is_super_admin === 1 ||
    this.currentUser()?.is_useradmin === 1 ||
    (this.isGroupAdmin() && this.userHelper.isGroupAdminCompanyMode())
  );
  isLocalUser = computed(() => this.currentUser()?.is_local_user || false);
  isMaintenanceUser = computed(() => this.currentUser()?.is_maintenance_user || false);
  isReadonlyUser = computed(() => this.currentUser()?.is_readonly_user || false);

  // Legacy properties for compatibility
  searchList: any[] = [];
  searchText: any;

  constructor(private host: ElementRef<HTMLElement>) { }

  ngOnInit() {
    localStorage.removeItem('productSearch');
    this.searchList = [];

    const data = localStorage.getItem('currentUser');
    const productlist = localStorage.getItem('navigateproductlist');

    this.getActiveLink();

    if (productlist === 'set') {
      localStorage.removeItem('navigateproductlist');
      this.isProducts.set(true);
    }

    if (!data) {
      this.logout();
      return;
    }

    this.currentUser.set(this.userHelper.applyGroupAdminPortalRights(JSON.parse(data)));
    if (this.currentUser()) {
      localStorage.setItem('currentUser', JSON.stringify(this.currentUser()));
    }
    this.selectedOrganisation.set(JSON.parse(localStorage.getItem('rootOrganisation') || 'null'));
    this.selectedDepartment.set(JSON.parse(localStorage.getItem('selectedDepartment') || 'null'));

    // Pure group admin in koncern mode should not stay on company portal routes
    if (this.userHelper.isGroupAdminInKoncernMode(this.currentUser())) {
      const url = this.router.url || '';
      if (!url.includes('/koncern') && !url.includes('/login') && !url.startsWith('/settings') && !url.startsWith('/editgroupuser')) {
        this.router.navigate(['/koncern']);
      }
    }

    // Set subscription flags
    this.subscribesToCatalogue.set(this.utilService.subscribesTo('subscription_catalogue'));
    this.subscribesToActivities.set(this.utilService.subscribesTo('subscription_activities'));
    this.subscribesToChecklist.set(this.utilService.subscribesTo('subscription_checklist'));
    this.subscribesToRiskAssessment.set(this.utilService.subscribesTo('subscription_risk_assessment'));
    this.subscribesToProjects.set(this.utilService.subscribesTo('subscription_projects'));
    this.subscribesToExposures.set(this.utilService.subscribesTo('subscription_exposures'));
    this.subscribesToPurchase.set(this.utilService.subscribesTo('subscription_purchase'));
    this.subscribesToSubstitution.set(this.utilService.subscribesTo('subscription_substitution'));
    this.subscribesToDistribution.set(this.utilService.subscribesTo('subscription_distribution'));
    this.subscribesToSamtec.set(this.utilService.subscribesTo('subscription_samtec'));
    this.subscribesToLawLawpoint.set(this.utilService.subscribesTo('subscription_lawpoint'));
    this.subscribesToLawAptor.set(this.utilService.subscribesTo('subscription_law_aptor'));
    this.subscribesToCoordinator.set(this.utilService.subscribesTo('subscription_coordinator'));
    this.subscribesToProducts.set(this.utilService.subscribesTo('subscription_products'));
    this.subscribesToReports.set(this.utilService.subscribesTo('subscription_reports'));
    this.subscribesToEducation.set(this.utilService.subscribesTo('subscription_education'));
    this.subscribesToProductApplication.set(this.utilService.subscribesTo('subscription_product_application'));
    this.subscribesToInventory.set(this.utilService.subscribesTo('subscription_inventory'));

    this.setupUserOrganisations();
    this.setupOrganisationSubscriptions();

    this.navStateService.navUrlChanged.subscribe((url: string) => {
      if (url === '/productlist') {
        this.gotoProducts();
      }
      else if (url === '/assessmentschemical') {
        this.goToAssessmentChemical();
      }
      else if (url === '/assessments') {
        this.goToAssessment();
      }
      else if (url === '/substitution') {
        this.goToSubstitution();
      }
      else if (url === '/exposures') {
        this.goToExposures();
      }
      else if (url === '/history') {
        this.goToHistory();
      }
      else if (url === '/product-application') {
        this.goToProductApplication();
      }
      else if (url === '/reports') {
        this.gotoReports();
      }
      else if (url === '/material-list') {
        this.gotoMaterialList();
      }
      else if (url === '/inventory') {
        this.gotoInventoryList();
      }
      else if (url === '/law') {
        this.gotoLaw();
      }
      else if (url === '/education') {
        this.gotoEducation();
      }
      else if (url === '/webshop') {
        this.gotoWebshop();
      }
      else if (url === '/samtec') {
        this.gotoSamtec();
      }
      else if (url === '/controlpanel') {
        this.gotoControlPanel();
      }
      else if (url === '/dashboard') {
        this.navigateHome();
      }
      else if (url === '/catalogue') {
        this.gotoCatalogue();
      }
      else if (url === '/coordinator') {
        this.goToCoordinator();
      }
      else if (url === '/substitution') {
        this.goToSubstitution();
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const clickedInside = this.host.nativeElement.contains(event.target as Node);
    if (!clickedInside) {
      this.isAdministrationDropdownOpen.set(false);
      this.isAssessmentDropdownOpen.set(false);
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.isAdministrationDropdownOpen.set(false);
      this.isAssessmentDropdownOpen.set(false);
    }
  }

  private setupUserOrganisations() {
    if (this.isGroupAdmin() && !this.isSuperUser() && !this.isSuperAdmin()) {
      const groupId = this.currentUser()?.group_id;
      if (!groupId) {
        this.organisationList.set([]);
        return;
      }
      this.organisationService.getAllCompanies({ group_id: groupId }).pipe(take(1)).subscribe({
        next: (response) => {
          let organisations: any[] = [];
          if (Array.isArray(response)) {
            organisations = response;
          } else if (Array.isArray(response?.companies)) {
            organisations = response.companies;
          } else if (Array.isArray(response?.data)) {
            organisations = response.data;
          }
          this.organisationList.set(organisations);
        },
        error: () => this.organisationList.set([]),
      });
      return;
    }

    if (this.isSuperUser() || this.isSuperAdmin()) {
      let hasValidCachedOrgs = false;
      // Check localStorage first for instant display
      const cachedOrgs = localStorage.getItem('cachedOrganisationList');
      if (cachedOrgs) {
        try {
          const parsed = JSON.parse(cachedOrgs);
          if (Array.isArray(parsed) && parsed.length > 0) {
            this.organisationList.set(parsed);
            hasValidCachedOrgs = true;
          }
        } catch (e) {
          // Invalid cache, ignore and fetch fresh
        }
      }

      // Always fetch fresh data from backend. Retry once if first attempt returns null/invalid.
      const fetchCompanies = (attempt: number): void => {
        this.organisationService.getAllCompanyNames();
        this.organisationService.getOrganisationNavDataListener().pipe(take(1)).subscribe({
          next: organisations => {
            if (Array.isArray(organisations)) {
              this.organisationList.set(organisations);
              // Update cache with fresh data
              localStorage.setItem('cachedOrganisationList', JSON.stringify(organisations));
              return;
            }

            if (attempt < 2) {
              fetchCompanies(attempt + 1);
              return;
            }

            this.clearOrg();
            this.organisationList.set([]);
            // Clear cache if no data after retry
            localStorage.removeItem('cachedOrganisationList');
          },
          error: () => {
            if (attempt < 2) {
              fetchCompanies(attempt + 1);
              return;
            }
            // If no valid cache exists, clear both context and organisation list.
            // If cache exists, keep it so user can still select organisation.
            if (!hasValidCachedOrgs) {
              this.clearOrg();
              this.organisationList.set([]);
            }
          }
        });
      };

      fetchCompanies(1);
    }
  }

  private setupOrganisationSubscriptions() {
    // Catalogue subscription
    this.organisationService.setCatalogueList.pipe(take(1)).subscribe(() => {
      this.gotoCatalogue();
      this.ngOnInit();
    });

    // Products subscription
    this.organisationService.setProductList.pipe(take(1)).subscribe(() => {
      this.gotoProducts();
      this.ngOnInit();
    });

    // Coordinator subscription
    this.organisationService.setCoordinatorList.pipe(take(1)).subscribe(() => {
      this.goToCoordinator();
      this.ngOnInit();
    });

    // Substitution subscription
    this.organisationService.setSubstitutionList.pipe(take(1)).subscribe(() => {
      this.goToSubstitution();
      this.ngOnInit();
    });

    // Risk Assessment Activity subscription
    this.organisationService.setRiskAssessmentActivityList.pipe(take(1)).subscribe(() => {
      this.goToAssessment();
      this.ngOnInit();
    });

    // Risk Assessment subscription
    this.organisationService.setRiskAssessmentList.pipe(take(1)).subscribe(() => {
      this.goToAssessmentChemical();
      this.ngOnInit();
    });
  }

  clearOrg() {
    this.selectedOrganisation.set(null);
    localStorage.removeItem("selectedOrganisation");
    localStorage.removeItem("selectedDepartment");
    localStorage.removeItem("allDepartments");
    localStorage.removeItem("changeOrg");
  }

  showSelectOrganisationModal() {
    this.clearActiveLinks();
    this.isChangeOrganisation.set(true);
    if (this.unsavedChangesService.getUnsavedChanges()) {
      this.unsavedChangesService.setUnsavedChanges(false);
      Swal.fire({
        title: this.translate.instant("LEAVE_BEFORE_SAVE"),
        text: this.translate.instant("LEAVE_BEFORE_SAVE_TEXT"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.translate.instant("OK"),
        cancelButtonText: this.translate.instant("ABORT"),
      }).then((result) => {
        if (result.isConfirmed) {
          localStorage.removeItem('productSearch');
          const modalRef = this.modalService.open(ModalSelectOrganisationComponent);
          modalRef.componentInstance.orgList = this.organisationList();
          if (this.isGroupCompanySwitcher()) {
            modalRef.componentInstance.titleKey = 'CHOOSE_GROUP_COMPANY';
          }
          modalRef.componentInstance.passEntry.pipe(take(1)).subscribe(() => {
            if (this.isGroupAdmin()) {
              this.userHelper.enterGroupAdminCompanyMode();
              const raw = localStorage.getItem('currentUser');
              if (raw) {
                localStorage.setItem(
                  'currentUser',
                  JSON.stringify(this.userHelper.applyGroupAdminPortalRights(JSON.parse(raw)))
                );
              }
              localStorage.setItem('changeOrg', 'true');
              this.isChangeOrganisation.set(false);
              window.location.href = '/dashboard';
              return;
            }
            localStorage.setItem('changeOrg', 'true');
            this.isChangeOrganisation.set(false);
            window.location.reload();
          });
        }
      });
    } else {
      localStorage.removeItem('productSearch');
      const modalRef = this.modalService.open(ModalSelectOrganisationComponent);
      modalRef.componentInstance.orgList = this.organisationList();
      if (this.isGroupCompanySwitcher()) {
        modalRef.componentInstance.titleKey = 'CHOOSE_GROUP_COMPANY';
      }
      modalRef.componentInstance.passEntry.pipe(take(1)).subscribe(() => {
        if (this.isGroupAdmin()) {
          this.userHelper.enterGroupAdminCompanyMode();
          const raw = localStorage.getItem('currentUser');
          if (raw) {
            localStorage.setItem(
              'currentUser',
              JSON.stringify(this.userHelper.applyGroupAdminPortalRights(JSON.parse(raw)))
            );
          }
          localStorage.setItem('changeOrg', 'true');
          this.isChangeOrganisation.set(false);
          window.location.href = '/dashboard';
          return;
        }
        localStorage.setItem('changeOrg', 'true');
        this.isChangeOrganisation.set(false);
        window.location.reload();
      });
    }
  }

  logout() {
    this.authService.logout();
  }

  getActiveLink() {
    const currentUrl = this.router.url;
    this.clearActiveLinks();

    if (currentUrl.includes('/koncern')) {
      this.isKoncernHome.set(true);
    } else if (currentUrl.includes('/dashboard')) {
      this.isHome.set(true);
    } else if (currentUrl.includes('/catalogue')) {
      this.isCatalogue.set(true);
    } else if (currentUrl.includes('/productlist')) {
      this.isProducts.set(true);
    } else if (currentUrl.includes('/material-list')) {
      this.isMaterialList.set(true);
    } else if (currentUrl.includes('/inventory')) {
      this.isInventory.set(true);
    } else if (currentUrl.includes('/reports')) {
      this.isReports.set(true);
    } else if (currentUrl.includes('/assessment')) {
      this.isAssessment.set(true);
    } else if (currentUrl.includes('/coordinator') || currentUrl.includes('/exposures') ||
               currentUrl.includes('/substitution') || currentUrl.includes('/history') ||
               currentUrl.includes('/product-application')) {
      this.isAdministration.set(true);
    } else if (currentUrl.includes('/law')) {
      this.isLaw.set(true);
    } else if (currentUrl.includes('/samtec')) {
      this.isSamtec.set(true);
    } else if (currentUrl.includes('/education')) {
      this.isEducation.set(true);
    } else if (currentUrl.includes('/control-panel')) {
      this.isControlPanel.set(true);
    }
  }

  clearActiveLinks() {
    this.isChangeOrganisation.set(false);
    this.isHome.set(false);
    this.isKoncernHome.set(false);
    this.isCatalogue.set(false);
    this.isProducts.set(false);
    this.isMaterialList.set(false);
    this.isInventory.set(false);
    this.isReports.set(false);
    this.isAssessment.set(false);
    this.isAdministration.set(false);
    this.isLaw.set(false);
    this.isSamtec.set(false);
    this.isEducation.set(false);
    this.isWebshop.set(false);
    this.isControlPanel.set(false);
    // Close all dropdowns when navigating to other menu items
    this.isAdministrationDropdownOpen.set(false);
    this.isAssessmentDropdownOpen.set(false);
  }

  navigateHome() {
    this.clearActiveLinks();
    if (this.isKoncernMode()) {
      this.isKoncernHome.set(true);
      this.router.navigate(['/koncern']);
      return;
    }
    if (this.isGroupAdmin() && this.userHelper.isGroupAdminCompanyMode()) {
      // Company mode: normal dashboard
      this.isHome.set(true);
      this.router.navigate(['/dashboard']);
      return;
    }
    this.isHome.set(true);
    this.router.navigate(['/dashboard']);
  }

  gotoKoncernHome() {
    this.clearActiveLinks();
    this.userHelper.exitGroupAdminCompanyMode();
    const raw = localStorage.getItem('currentUser');
    if (raw) {
      const user = JSON.parse(raw);
      // Drop session elevation; keep is_group_admin
      if (this.userHelper.isGroupAdmin(user)) {
        // Do not force is_admin=0 if they are also a real company admin on home org —
        // reload from stored flags: strip only if we elevated. Simplest: set is_admin from
        // not treating as elevated — user object may already have is_admin=1 from previous elevation.
        // Re-fetch isn't available; set is_admin=0 for pure group admins without other admin flags.
        if (
          !this.userHelper.isTruthyFlag(user.is_super_user) &&
          !this.userHelper.isTruthyFlag(user.is_super_admin) &&
          !this.userHelper.isTruthyFlag(user.is_local_admin)
        ) {
          // Pure group admin: leave company mode without company-admin rights
          user.is_admin = 0;
          localStorage.setItem('currentUser', JSON.stringify(user));
          this.currentUser.set(user);
        }
      }
    }
    this.isKoncernHome.set(true);
    this.router.navigate(['/koncern']).then(() => window.location.reload());
  }

  gotoCatalogue() {
    this.clearActiveLinks();
    this.isCatalogue.set(true);
    this.router.navigate(['/catalogue']);
  }

  gotoProducts() {
    this.clearActiveLinks();
    this.isProducts.set(true);
    this.router.navigate(['/productlist']);
  }

  goToCoordinator() {
    this.clearActiveLinks();
    this.isAdministration.set(true);
    this.isAdministrationDropdownOpen.set(false);
    // Dashboard (and similar) may set coordinator.filter.status before navigating here.
    // In that case, keep the requested filter instead of resetting to "show all".
    // Do not set fromNavigationLink — that is reserved for top-header one-off include.
    const hasStatusFilter = sessionStorage.getItem('coordinator.filter.status') != null;
    if (!hasStatusFilter) {
      sessionStorage.removeItem('coordinator.search.all');
      sessionStorage.removeItem('coordinator.filter.status');
      sessionStorage.removeItem('coordinator.filter.prio');
    }
    this.router.navigate(['/coordinator']);
  }

  goToSubstitution() {
    this.clearActiveLinks();
    this.isAdministration.set(true);
    this.isAdministrationDropdownOpen.set(false);
    this.router.navigate(['/substitution']);
  }

  goToExposures() {
    this.clearActiveLinks();
    this.isAdministration.set(true);
    this.isAdministrationDropdownOpen.set(false);
    this.router.navigate(['/exposures']);
  }

  goToHistory() {
    this.clearActiveLinks();
    this.isAdministration.set(true);
    this.isAdministrationDropdownOpen.set(false);
    this.router.navigate(['/history']);
  }

  goToProductApplication() {
    this.clearActiveLinks();
    this.isAdministration.set(true);
    this.isAdministrationDropdownOpen.set(false);
    this.router.navigate(['/productapplicationlist']);
  }

  goToAssessment() {
    this.clearActiveLinks();
    this.isAssessment.set(true);
    this.isAssessmentDropdownOpen.set(false);
    this.router.navigate(['/assessments']);
  }

  goToAssessmentChemical() {
    this.clearActiveLinks();
    this.isAssessment.set(true);
    this.isAssessmentDropdownOpen.set(false);
    this.router.navigate(['/assessmentschemical']);
  }

  gotoReports() {
    this.clearActiveLinks();
    this.isReports.set(true);
    this.router.navigate(['/reports']);
  }

  gotoMaterialList() {
    this.clearActiveLinks();
    this.isMaterialList.set(true);
    this.router.navigate(['/materiallist']);
  }

  gotoInventoryList() {
    this.clearActiveLinks();
    this.isInventory.set(true);
    this.router.navigate(['/inventorylist']);
  }

  gotoLaw() {
    this.clearActiveLinks();
    this.isLaw.set(true);
    this.router.navigate(['/law']);
  }

  gotoEducation() {
    this.clearActiveLinks();
    this.isEducation.set(true);
    var link="https://portal.cloocast.com"
    window.open(link,'_blank');
  }

  gotoWebshop() {
    this.clearActiveLinks();
    this.isWebshop.set(true);
    var link="https://kemikaliedokumentation.se/butik/"
    window.open(link,'_blank');
  }

  gotoSamtec() {
    this.clearActiveLinks();
    this.isSamtec.set(true);
    if(!this.currentUser().samtectoken){
      window.open('https://portal.samtec.se/','_blank');
      return;
    }
    var link=this.translate.instant("SAMTEC_LINK_PROD")+this.currentUser().samtectoken;
    window.open(link,'_blank');
  }

  gotoControlPanel() {
    this.clearActiveLinks();
    this.isControlPanel.set(true);
    this.router.navigate(['/controlpanel']);
  }

  toggleAdministrationDropdown() {
    const nextState = !this.isAdministrationDropdownOpen();
    this.isAdministrationDropdownOpen.set(nextState);
    if (nextState) {
      this.isAssessmentDropdownOpen.set(false);
    }
  }

  toggleAssessmentDropdown() {
    const nextState = !this.isAssessmentDropdownOpen();
    this.isAssessmentDropdownOpen.set(nextState);
    if (nextState) {
      this.isAdministrationDropdownOpen.set(false);
    }
  }
}

